import { AdyenEnvironment } from "@redotech/congo-enums/adyen-environment";
import { HsaAdyenOnboardingStatus } from "@redotech/crdb-enums/conversion/hsa-adyen-onboarding-status";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** The generated `adyen_environment` values, typed as the `"test" | "live"` union the Adyen config uses. */
const adyenEnvironmentSchema = z.templateLiteral([
  createEnum(AdyenEnvironment),
]);

/** Adyen's `organization.type` values Generate KYC offers; mirrors `AdyenOrganizationType` in `redo/payments/adyen`. */
const adyenOrganizationTypeSchema = z.enum([
  "privateCompany",
  "partnershipIncorporated",
  "listedPublicCompany",
  "nonProfit",
]);

const hsaAdyenOnboardingAccountProjection_ = z.object({
  environment: createEnum(AdyenEnvironment),
  legalEntityId: z.string().nullable(),
  accountHolderId: z.string().nullable(),
  balanceAccountId: z.string().nullable(),
  storeId: z.string().nullable(),
  businessLines: z.array(
    z.object({
      mcc: z.string(),
      industryCode: z.string(),
      adyenBusinessLineId: z.string().nullable(),
      tier: z.string().nullable(),
    }),
  ),
  onboardingStatus: createEnum(HsaAdyenOnboardingStatus),
  capabilities: z.array(
    z.object({
      name: z.string(),
      allowed: z.boolean(),
      enabled: z.boolean(),
      verificationStatus: z.string(),
    }),
  ),
  problems: z.array(z.object({ code: z.string(), message: z.string() })),
  paymentMethodsRequestedAt: z.date().nullable(),
  galeMirroredAt: z.date().nullable(),
  kycGeneratedBy: z.string().nullable(),
  kycGeneratedAt: z.date().nullable(),
  lastCheckedAt: z.date().nullable(),
  /** Manual for a Canadian bank account, which Adyen's automatic payout schedule can't pay. */
  payoutScheduleSetup: z.enum(["automatic", "manual"]),
});
/** The admin view of a team's Redo-owned Adyen account in one environment. */
export const hsaAdyenOnboardingAccountProjection: z.ZodType<HsaAdyenOnboardingAccountProjection> =
  hsaAdyenOnboardingAccountProjection_;
export interface HsaAdyenOnboardingAccountProjection extends z.infer<
  typeof hsaAdyenOnboardingAccountProjection_
> {}

const hsaAdyenOnboardingPrefill_ = z.object({
  environment: adyenEnvironmentSchema,
  legalName: z.string().nullable(),
  /** Null means it isn't sent. */
  doingBusinessAs: z.string().nullable(),
  /** Null means it isn't sent. */
  organizationType: adyenOrganizationTypeSchema.nullable(),
  shopName: z.string(),
  country: z.string().nullable(),
  registeredAddress: z
    .object({
      street: z.string(),
      street2: z.string().nullable(),
      city: z.string(),
      stateOrProvince: z.string(),
      postalCode: z.string(),
      country: z.string(),
    })
    .nullable(),
  supportEmail: z.string().nullable(),
  /** Null means it isn't sent. */
  businessEmail: z.string().nullable(),
  phone: z.string().nullable(),
  /** Null means it isn't sent. */
  businessPhone: z.string().nullable(),
  website: z.string().nullable(),
  statementDescriptor: z.string().nullable(),
  ein: z.string().nullable(),
  myshopifyDomain: z.string(),
  lines: z.array(
    z.object({
      role: z.enum(["business", "healthcare"]),
      mcc: z.string(),
      industryCode: z.string(),
      tier: z.string(),
      description: z.string(),
    }),
  ),
  preflight: z.array(
    z.object({
      id: z.string(),
      severity: z.enum(["block", "warning"]),
      message: z.string(),
    }),
  ),
});
/** What Generate KYC will send, before the operator's edits. Null means nothing usable was found. */
export const hsaAdyenOnboardingPrefill: z.ZodType<HsaAdyenOnboardingPrefillProjection> =
  hsaAdyenOnboardingPrefill_;
export interface HsaAdyenOnboardingPrefillProjection extends z.infer<
  typeof hsaAdyenOnboardingPrefill_
> {}

/** The operator's Generate KYC edits; an absent field keeps the prefill, and an empty EIN clears it. */
const hsaKycOverrides = z.object({
  legalName: z.string().optional(),
  statementDescriptor: z.string().optional(),
  phone: z.string().optional(),
  /** Empty clears it, so it isn't sent. */
  businessPhone: z.string().optional(),
  /** Empty clears it, so it isn't sent. */
  businessEmail: z.string().optional(),
  /** Empty clears it, so it isn't sent. */
  doingBusinessAs: z.string().optional(),
  organizationType: adyenOrganizationTypeSchema.optional(),
  supportEmail: z.string().optional(),
  website: z.string().optional(),
  ein: z.string().optional(),
  registeredAddress: z
    .object({
      street: z.string(),
      street2: z.string().optional(),
      city: z.string(),
      /** A code or a name; empty in Canada takes the postal code's province. */
      state: z.string(),
      postalCode: z.string(),
      /** The prefill's country; the server refuses one that isn't the shop's. */
      country: z.string(),
    })
    .optional(),
});

/** The prefill and preflight for the Generate KYC modal, read live from Shopify; with edits, a preview of them. */
export const getHsaAdyenOnboardingPrefill = {
  name: "getHsaAdyenOnboardingPrefill" as const,
  input: z.object({
    teamId: zExt.objectId(),
    overrides: hsaKycOverrides.optional(),
  }),
  output: z.object({
    configured: z.boolean(),
    prefill: hsaAdyenOnboardingPrefill,
  }),
};

/**
 * Creates the Gale merchant and every Adyen resource, locks the lines, then
 * (when enabled) mirrors to Gale. A retry resumes; a finished one is refused.
 */
export const generateHsaKyc = {
  name: "generateHsaKyc" as const,
  input: hsaKycOverrides.extend({ teamId: zExt.objectId() }),
  output: z.object({ account: hsaAdyenOnboardingAccountProjection }),
};

/** The team's Redo-owned Adyen account in this environment, or null before Generate KYC. */
export const getHsaAdyenOnboarding = {
  name: "getHsaAdyenOnboarding" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: z.object({
    account: hsaAdyenOnboardingAccountProjection.nullable(),
    /** The `hsa-adyen-gale-mirror` flag, so the card can say why an account isn't on Gale yet. */
    galeMirrorEnabled: z.boolean(),
  }),
};

/** Hands Gale the stored Adyen ids again; Gale takes a replay of the same ids. */
export const mirrorHsaAdyenAccountToGale = {
  name: "mirrorHsaAdyenAccountToGale" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: z.object({ account: hsaAdyenOnboardingAccountProjection }),
};

/** Reads the account holder from Adyen now and stores its status, as the ten-minute poll does. */
export const refreshHsaAdyenOnboardingStatus = {
  name: "refreshHsaAdyenOnboardingStatus" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: z.object({ account: hsaAdyenOnboardingAccountProjection }),
};
